import moment from 'moment-timezone';
import { RRule, Weekday } from 'rrule';
import { DayKey } from '../../ProfileDetailsCard/formatWorkingHours';

const RRULE_WEEKDAY_TO_DAY_KEY: DayKey[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];

export const getWeeklyDaysFromRRule = (rruleString: string): number[] => {
  try {
    const rrule = RRule.fromString(rruleString);

    if (rrule.options.freq !== RRule.WEEKLY) {
      return [];
    }

    if (!rruleString.includes('BYDAY=')) {
      return [];
    }

    const byweekday = rrule.options.byweekday;
    if (!byweekday) {
      return [];
    }

    const daysArray = Array.isArray(byweekday) ? byweekday : [byweekday];

    return daysArray
      .map((day) => {
        if (day && typeof day === 'object' && 'weekday' in day) {
          return (day as Weekday).weekday;
        }
        return day as number;
      })
      .sort((a, b) => a - b);
  } catch {
    return [];
  }
};

export const getScheduledDaysFromRecurrence = (
  recurrence: string | null | undefined
): DayKey[] => {
  if (!recurrence) {
    return [];
  }
  return getWeeklyDaysFromRRule(recurrence)
    .map((index) => RRULE_WEEKDAY_TO_DAY_KEY[index])
    .filter((day): day is DayKey => !!day);
};

export const resolveZone = (timeZone: string | null | undefined): string =>
  timeZone && moment.tz.zone(timeZone) ? timeZone : moment.tz.guess();

export const isDateInWeeklyRecurrence = (
  recurrence: string | null | undefined,
  date: moment.Moment,
  timeZone: string | null | undefined
): boolean => {
  const scheduledDays = recurrence ? getWeeklyDaysFromRRule(recurrence) : [];
  if (scheduledDays.length === 0) {
    return false;
  }

  const zone = resolveZone(timeZone);
  const dayIndex = date.clone().tz(zone).isoWeekday() - 1;
  return scheduledDays.includes(dayIndex);
};

export const formatReservationTimeRange = (
  startTime: string,
  endTime: string | null | undefined,
  timeZone: string | null | undefined
): string => {
  const zone = resolveZone(timeZone);
  const start = moment.tz(startTime, moment.ISO_8601, true, zone);
  if (!start.isValid()) {
    return '';
  }
  const end = endTime ? moment.tz(endTime, moment.ISO_8601, true, zone) : null;
  if (!end || !end.isValid()) {
    return start.format('h:mm a');
  }
  return `${start.format('h:mm a')} - ${end.format('h:mm a')}`;
};

/** The ISO calendar day (in the reservation's zone) used to group by date. */
export const getReservationDayKey = (
  startTime: string,
  timeZone: string | null | undefined
): string => {
  const zone = resolveZone(timeZone);
  const start = moment.tz(startTime, moment.ISO_8601, true, zone);
  return start.isValid() ? start.format('YYYY-MM-DD') : startTime;
};

/** The date format used for a booking's non-today dates, e.g. "Tue, Apr 7". */
const DATE_FORMAT = 'ddd, MMM D';

/**
 * A translation key under `peopleProfileSidebar.bookings.dateSummary` plus the
 * values to interpolate into it. The date logic returns this rather than a
 * finished string so it stays unit-testable without an i18n runtime.
 */
export type BookingDateSummary = {
  key: string;
  values: Record<string, string | number>;
};

/** The part of a reservation a date summary is derived from. */
export type BookingDateSummaryInstance = {
  startTime: string;
  endTime?: string | null;
  timeZone?: string | null;
};

/**
 * Whether a reservation covers a whole day: it starts at midnight and ends
 * either at 11:59 pm or at midnight the next day.
 */
export const isFullDayBooking = (
  start: moment.Moment,
  end: moment.Moment | null
): boolean => {
  if (!end) {
    return false;
  }
  return (
    start.clone().startOf('day').isSame(start, 'minute') &&
    (start.clone().endOf('day').isSame(end, 'minute') ||
      start.clone().add(1, 'day').startOf('day').isSame(end, 'minute'))
  );
};

/**
 * Whether the given dates are one unbroken run of calendar days — which is
 * what separates a booking shown as a range ("Mon – Fri") from one shown as a
 * count of scattered days ("Mon and 3 more days"). Duplicate dates are not
 * consecutive.
 */
export const areBookingDatesConsecutive = (dates: moment.Moment[]): boolean => {
  if (dates.length < 2) {
    return false;
  }
  for (let index = 1; index < dates.length; index++) {
    const previousDay = dates[index - 1].clone().startOf('day');
    const currentDay = dates[index].clone().startOf('day');
    if (currentDay.diff(previousDay, 'days') !== 1) {
      return false;
    }
  }
  return true;
};

/**
 * The date + time label for a booking in the fullscreen Desks panel.
 *
 * A multi-day booking is stored as one instance per day sharing a `seriesId`,
 * so the caller passes every instance of one booking and gets a single label
 * summarizing the whole span — the same shapes the legacy profile page
 * produced from `SEAT_RESERVATION_STATUS`:
 *
 * - "Today, full day" / "Today, 9:00 am - 5:00 pm"
 * - "Today – Fri, Apr 10, full days" (consecutive days)
 * - "Today and Fri, Apr 10, full days" (two scattered days)
 * - "Today and 3 more days, 9:00 am - 5:00 pm" (three or more scattered days)
 * - "Today, 9:00 am – Fri, Apr 10, 5:00 pm" (one reservation spanning days)
 *
 * Instances are assumed to share a time of day, as the booking flows produce;
 * the first instance's times are the ones shown.
 */
export const getBookingDateSummary = (
  instances: BookingDateSummaryInstance[],
  todayLabel: string,
  now: moment.Moment = moment()
): BookingDateSummary | null => {
  if (instances.length === 0) {
    return null;
  }

  const zone = resolveZone(instances[0].timeZone);
  const dated = instances
    .map((instance) => ({
      instance,
      start: moment.tz(instance.startTime, moment.ISO_8601, true, zone),
    }))
    .filter(({ start }) => start.isValid())
    .sort((left, right) => left.start.diff(right.start));
  if (dated.length === 0) {
    return null;
  }

  // The earliest instance is the one the label leads with, and the one whose
  // times stand for the booking.
  const { instance: first, start: firstStart } = dated[0];
  const starts = dated.map(({ start }) => start);

  const parsedEnd = first.endTime
    ? moment.tz(first.endTime, moment.ISO_8601, true, zone)
    : null;
  const end = parsedEnd?.isValid() ? parsedEnd : null;
  const range = formatReservationTimeRange(
    first.startTime,
    first.endTime,
    first.timeZone
  );
  const isFullDay = isFullDayBooking(firstStart, end);
  // Only the first date collapses to "Today" — an end date is by definition a
  // different day, so spelling it out reads better there.
  const startDate = firstStart.isSame(now.clone().tz(zone), 'day')
    ? todayLabel
    : firstStart.format(DATE_FORMAT);

  if (starts.length === 1) {
    // A single reservation can still span days (the legacy range booking:
    // one reservation from Monday morning to Friday evening).
    if (end && !isFullDay && !end.isSame(firstStart, 'day')) {
      return {
        key: 'span',
        values: {
          startDate,
          startTime: firstStart.format('h:mm a'),
          endDate: end.format(DATE_FORMAT),
          endTime: end.format('h:mm a'),
        },
      };
    }
    return isFullDay
      ? { key: 'fullDay', values: { date: startDate } }
      : { key: 'timed', values: { date: startDate, range } };
  }

  const endDate = starts[starts.length - 1].format(DATE_FORMAT);

  if (areBookingDatesConsecutive(starts)) {
    return isFullDay
      ? { key: 'consecutiveFullDays', values: { startDate, endDate } }
      : { key: 'consecutiveTimed', values: { startDate, endDate, range } };
  }

  // Two scattered days name both dates; beyond that the dates stop fitting, so
  // the rest become a count.
  if (starts.length === 2) {
    return isFullDay
      ? { key: 'twoDatesFullDays', values: { startDate, endDate } }
      : { key: 'twoDatesTimed', values: { startDate, endDate, range } };
  }

  const dayCount = starts.length - 1;
  return isFullDay
    ? { key: 'moreFullDays', values: { startDate, dayCount } }
    : { key: 'moreDays', values: { startDate, dayCount, range } };
};
